import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import {
    User,
    Mail,
    Clock,
    Shield,
    Building2,
    Globe,
    CheckCircle
} from "lucide-react";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";

export default function AccountPage() {
    const userData = useUserStore((state) => state.user);

    const realm = myLocalStorage.getItem("realm") || "";

    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const { email, role, userRole, userRoles } = useUserStore((state) => state.user);

    const effectiveRole = Array.isArray(userRoles) ? userRoles : userRoles ?? userRole ?? "";

    const hasRole = (roleName: string) => {
        if (Array.isArray(effectiveRole)) return effectiveRole.includes(roleName);
        if (typeof effectiveRole === "string") return effectiveRole.includes(roleName);
        return false;
    };

    const getPermission = () => {
        if (hasRole("whiteswan-admin") || role === "SYS_ADMIN") {
            return ["Full Access", "User Management", "Request Approvals", "System Configuration", "Audit Logs"]
        } else if (hasRole("server-pam-admin")) {
            return ["Server Machine Access", "Server User Management", "Server Request Approvals", "System Configuration", "Server Audit Logs"]
        } else if (hasRole("epam-admin")) {
            return ["Endpoint Machine Access", "Endpoint User Management", "Endpoint Request Approvals", "System Configuration", "Endpoint Audit Logs"]
        } else if (hasRole("epam-user")) {
            return ["Endpoint Resource Request", "Cloud Resource Request"]
        } else {
            return ["Servers Connect Request", "Servers Resource Request", "Cloud Resource Request"]
        }
    }

    const accountData = {
        permissions: getPermission()
    };

    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                    <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                        Account Details
                    </h1>
                    <p className="text-muted-foreground">
                        View your account information and access details
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Overview */}
                <div className="lg:col-span-1">
                    <Card className="border-border/40">
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-4">
                                <Avatar className="w-24 h-24">
                                    <AvatarImage src="" />
                                    <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-2xl font-semibold">
                                        WS
                                    </AvatarFallback>
                                </Avatar>
                            </div>
                            <CardTitle className="text-xl">{email}</CardTitle>
                            <div className="flex items-center justify-center gap-2 mt-2">
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Active
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <div>
                                        {(() => {
                                            const roles = Array.isArray(userRoles)
                                                ? userRoles.map((r: string) => String(r).trim()).filter((r: string) => r)
                                                : typeof (userRoles || userRole) === "string"
                                                    ? (userRoles || userRole || "").split(',').map((r: string) => r.trim()).filter((r: string) => r)
                                                    : [];
                                            if (roles.length > 1) {
                                                return (
                                                    <div className="flex flex-wrap gap-2">
                                                        {roles.map((role: string, index: number) => (
                                                            <Badge
                                                                key={index}
                                                                variant="secondary"
                                                                className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
                                                            >
                                                                {role}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                );
                                            } else {
                                                return <p className="text-sm font-medium">{roles[0] || ''}</p>;
                                            }
                                        })()}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <div>
                                        <p className="text-sm font-medium">{tenantName}</p>
                                        <p className="text-xs text-muted-foreground">Tenant Name</p>
                                    </div>
                                </div>


                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Account Information */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Contact Information */}
                    <Card className="border-border/40">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                Contact Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                                    <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm">{email}</span>
                                    </div>
                                </div>

                            </div>
                        </CardContent>
                    </Card>



                    {/* Permissions */}
                    <Card className="border-border/40">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                Access Permissions
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    Your account has the following permissions within the White Swan Security platform:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {accountData.permissions.map((permission, index) => (
                                        <Badge
                                            key={index}
                                            variant="secondary"
                                            className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
                                        >
                                            {permission}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Security Information */}
                    <Card className="border-border/40">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                Security Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-muted-foreground">{realm.includes("legacy") ? "MFA Authentication" : role === "SYS_ADMIN" ? "Strong Password" : "Passkey Authentication"}</label>
                                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="text-sm text-green-700 dark:text-green-300">Enabled</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-muted-foreground">Session Timeout</label>
                                    <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm">8 hours</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}